import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, GripVertical, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownEditor from '../components/MarkdownEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminChapters = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingChapter, setEditingChapter] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchChapters();
  }, []);

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API_URL}/chapters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChapters(response.data.sort((a, b) => a.index - b.index));
    } catch (error) {
      console.error('Erreur chargement chapitres:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (chapterData) => {
    try {
      if (editingChapter?.id) {
        await axios.put(
          `${API_URL}/admin/chapters/${editingChapter.id}`,
          chapterData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Chapitre mis à jour');
      } else {
        await axios.post(
          `${API_URL}/admin/chapters`,
          chapterData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Chapitre créé');
      }
      fetchChapters();
      setIsDialogOpen(false);
      setEditingChapter(null);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (chapterId) => {
    if (!window.confirm('Supprimer ce chapitre ?')) return;

    try {
      await axios.delete(`${API_URL}/admin/chapters/${chapterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chapitre supprimé');
      fetchChapters(); // Refetch
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/chapters/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.count} chapitres réinitialisés`);
      setIsResetDialogOpen(false);
      fetchChapters(); // Refetch
    } catch (error) {
      console.error('Erreur reset:', error);
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-sand-50">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/contents')}
          className="text-white hover:bg-sage-600 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-4xl font-serif font-light mb-2">Chapitres Méthode</h1>
        <p className="text-white/80 font-sans">Gérer les 10 chapitres explicatifs</p>
      </div>

      {/* Add button */}
      <div className="px-6 py-6 space-y-3">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setEditingChapter(null);
                setIsDialogOpen(true);
              }}
              className="w-full bg-sage-500 hover:bg-sage-600 text-white h-12 rounded-xl"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter un chapitre
            </Button>
          </DialogTrigger>
          <ChapterDialog
            chapter={editingChapter}
            onSave={handleSave}
            onClose={() => setIsDialogOpen(false)}
          />
        </Dialog>

        {/* Reset button */}
        <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full border-2 border-declic text-declic hover:bg-declic hover:text-white h-12 rounded-xl"
            >
              <RotateCcw className="w-5 h-5 mr-2" />
              Réinitialiser chapitres Déclic RePas
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-serif text-sage-700">
                Réinitialiser les chapitres Méthode ?
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sage-700 font-sans mb-4">
                Cette action va <strong>supprimer tous les chapitres existants</strong> et les remplacer par les 10 chapitres officiels "Déclic RePas" (version longue).
              </p>
              <p className="text-sage-700/70 font-sans text-sm">
                Chapitres : Anticipation du plaisir, Structuration repas, Gestion paradoxale, Plaisir mouvement, Redevenir gourmet, Assiettes dessert, Apéro, Flexibilité, Auto-évaluation, Patience tolérance
              </p>
            </div>
            <DialogFooter className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsResetDialogOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 bg-declic hover:bg-declic/90 text-white"
              >
                {resetting ? 'Réinitialisation...' : 'Oui, je réinitialise'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Chapters list */}
      <div className="px-6 space-y-3">
        {chapters.map((chapter) => (
          <div
            key={chapter.id}
            className="bg-white rounded-xl border border-sand-200 p-5"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <GripVertical className="w-5 h-5 text-sage-700/40 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-sans text-sage-700/60">Chapitre {chapter.index}</span>
                    <span className="text-xs bg-sage-100 text-sage-700 px-2 py-0.5 rounded font-mono">
                      {chapter.slug}
                    </span>
                  </div>
                  <h3 className="font-serif text-lg text-sage-700 mb-2">{chapter.title}</h3>
                  <p className="text-sm text-sage-700/60 font-sans">
                    {chapter.content.substring(0, 150)}...
                  </p>
                </div>
              </div>
              <div className="flex space-x-2 ml-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditingChapter(chapter);
                    setIsDialogOpen(true);
                  }}
                  className="text-sage-500 hover:text-sage-700"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(chapter.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChapterDialog = ({ chapter, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    index: chapter?.index ?? 1,
    title: chapter?.title ?? '',
    content: chapter?.content ?? ''
  });

  // Mettre à jour le formulaire quand on sélectionne un autre chapitre
  useEffect(() => {
    setFormData({
      index: chapter?.index ?? 1,
      title: chapter?.title ?? '',
      content: chapter?.content ?? ''
    });
  }, [chapter]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl font-serif text-sage-700">
          {chapter ? 'Modifier le chapitre' : 'Nouveau chapitre'}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Ordre (Index)</Label>
            <Input
              type="number"
              value={formData.index}
              onChange={(e) => setFormData({ ...formData, index: parseInt(e.target.value) })}
              required
              min="1"
            />
          </div>
        </div>

        <div>
          <Label>Titre</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Ex: Introduction à la méthode Déclic RePas"
            required
          />
        </div>

        <div>
          <Label>Contenu (Markdown)</Label>
          <MarkdownEditor
            value={formData.content}
            onChange={(content) => setFormData({ ...formData, content })}
            placeholder="# Titre&#10;&#10;Votre contenu en Markdown..."
            minHeight="400px"
          />
        </div>

        <div className="flex space-x-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button type="submit" className="flex-1 bg-sage-500 hover:bg-sage-600 text-white">
            Enregistrer
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default AdminChapters;
